import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import useDebouncedValue from "../../../app/shared/hooks/useDebouncedValue";
import { AutocompleteProduct } from "../../../app/stores/productStore";

interface SearchBoxProps {
  placeholder?: string;
  onSearchChange?: (value: string) => void;
  onAutocompleteSearch?: (value: string) => void;
  autocompleteSuggestions?: AutocompleteProduct[];
  loadingAutocomplete?: boolean;
  debounceMs?: number;
  autocompleteDebounceMs?: number;
  onSuggestionClick?: (suggestion: AutocompleteProduct) => void;
}

const SearchBox: React.FC<SearchBoxProps> = ({
  placeholder,
  onSearchChange,
  onAutocompleteSearch,
  autocompleteSuggestions = [],
  loadingAutocomplete = false,
  debounceMs = 800, // Debounce for searchProducts
  autocompleteDebounceMs = 600, // Debounce for autocomplete API
  onSuggestionClick,
}) => {
  const [search, setSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedSearch = useDebouncedValue(search, debounceMs);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const autocompleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Call autocomplete with debounce
  useEffect(() => {
    if (autocompleteTimerRef.current) {
      clearTimeout(autocompleteTimerRef.current);
    }
    if (onAutocompleteSearch) {
      autocompleteTimerRef.current = setTimeout(() => {
        onAutocompleteSearch(search);
      }, autocompleteDebounceMs);
    }
    return () => {
      if (autocompleteTimerRef.current) {
        clearTimeout(autocompleteTimerRef.current);
      }
    };
  }, [search, onAutocompleteSearch, autocompleteDebounceMs]);

  // Call searchProducts with debounce
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, onSearchChange]);

  useEffect(() => {
    if (!showSuggestions) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  // Keyboard accessibility: Close on Escape key
  useEffect(() => {
    if (!showSuggestions) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSuggestions]);

  const handleSuggestionClick = (suggestion: AutocompleteProduct) => {
    setSearch(suggestion.subcategory);
    setShowSuggestions(false);
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    // Explicitly show suggestions only when user types
    if (value.trim().length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setShowSuggestions(false);
  };

  const handleCloseSuggestions = () => {
    setShowSuggestions(false);
  };

  return (
    <Box my={2} ref={searchBoxRef} sx={{ position: "relative" }}>
      <Paper elevation={1} sx={{ p: 2 }}>
        <TextField
          fullWidth
          placeholder={placeholder}
          value={search}
          onChange={handleInputChange}
          variant="outlined"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loadingAutocomplete && search.trim().length > 0 ? (
                    <CircularProgress size={20} />
                  ) : (
                    search.trim().length > 0 && (
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                        aria-label="clear search"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
      </Paper>

      {/* Autocomplete Suggestions Dropdown */}
      <Collapse in={showSuggestions && search.trim().length > 0}>
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "400px",
            overflowY: "auto",
            mt: 0.5,
          }}
        >
          {/* Header with close button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              zIndex: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Products
            </Typography>
            <IconButton
              size="small"
              onClick={handleCloseSuggestions}
              aria-label="close suggestions"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {autocompleteSuggestions.length > 0 ? (
            <List disablePadding>
              {autocompleteSuggestions.map((suggestion) => (
                <Box key={suggestion.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        py: 0,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={600} fontSize="0.9rem">
                            <Box component="span" fontWeight={600}>
                              {suggestion.suggestionText}
                            </Box>
                            {" in "}
                            <Box component="span" fontWeight={600}>
                              {suggestion.subcategory}
                            </Box>
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0 }}
                          >
                            {suggestion.category}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </Box>
              ))}
              {/* Search in option */}
              <Box>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setShowSuggestions(false);
                      if (onSearchChange) onSearchChange(search);
                    }}
                    sx={{
                      py: 0.5,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="primary">
                          Search in "{search}"
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </Box>
            </List>
          ) : (
            !loadingAutocomplete && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No products found for "{search}".
                </Typography>
              </Box>
            )
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};

export default SearchBox;
